import styled from 'styled-components'
import Section, { SectionContainer, SectionHeader } from '../common/Section'

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing['3xl']};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`

const AboutImage = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.xl};

  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform ${({ theme }) => theme.transitions.slow};
  }

  &:hover {
    img {
      transform: scale(1.05);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: -1;
  }
`

const AboutText = styled.div`
  h3 {
    font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.text};
  }

  p {
    font-size: ${({ theme }) => theme.fonts.sizes.lg};
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`

const FeaturesList = styled.ul`
  list-style: none;
  margin-top: ${({ theme }) => theme.spacing.xl};

  li {
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    color: ${({ theme }) => theme.colors.textSecondary};

    &::before {
      content: '✓';
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      background: ${({ theme }) => theme.colors.success};
      border-radius: 50%;
      flex-shrink: 0;
      color: ${({ theme }) => theme.colors.text};
      font-size: 12px;
      font-weight: bold;
    }
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`

const Stat = styled.div`
  text-align: center;

  .number {
    font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  .label {
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`

const About = () => {
  return (
    <Section id="about" className="with-background">
      <SectionContainer>
        <SectionHeader>
          <h2>About Nexus Realms</h2>
          <p>A revolutionary gaming experience years in the making</p>
        </SectionHeader>

        <AboutContent>
          <AboutText>
            <h3>The Journey Begins</h3>
            <p>
              Nexus Realms is more than just a game—it's an epic adventure
              that spans across multiple dimensions. Set in a beautifully
              crafted open world, you'll embark on a journey that challenges
              your skills, judgment, and determination.
            </p>
            <p>
              With over 5 years of development, our team at Nexus Gaming Studios
              has poured their hearts into creating an unforgettable experience
              that pushes the boundaries of what's possible in gaming.
            </p>

            <FeaturesList>
              <li>Over 200 hours of gameplay</li>
              <li>Multiple endings based on your choices</li>
              <li>Deep character customization</li>
              <li>Regular free updates and expansions</li>
            </FeaturesList>
          </AboutText>

          <AboutImage>
            <img
              src="data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23667eea;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23764ba2;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23grad)' width='600' height='400'/%3E%3Ccircle cx='300' cy='200' r='100' fill='%23ff6b6b' opacity='0.3'/%3E%3Ccircle cx='150' cy='100' r='80' fill='%234ecdc4' opacity='0.3'/%3E%3Ccircle cx='450' cy='300' r='120' fill='%23f9a826' opacity='0.3'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='24' fill='white'%3ENexus Realms%3C/text%3E%3C/svg%3E"
              alt="Nexus Realms Game Art"
            />
          </AboutImage>
        </AboutContent>

        <StatsGrid>
          <Stat>
            <div className="number">500+</div>
            <div className="label">Npcs</div>
          </Stat>
          <Stat>
            <div className="number">50+</div>
            <div className="label">Hours of Story</div>
          </Stat>
          <Stat>
            <div className="number">10M+</div>
            <div className="label">Players</div>
          </Stat>
        </StatsGrid>
      </SectionContainer>
    </Section>
  )
}

export default About
